/**
 * Copyright (C) Mairistem
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 *
 * Proprietary and confidential
 */

import _ from 'lodash';
import React from 'react';
import { toast } from 'react-toastify';

import { request } from '@mairistem/tools';
import type { DocumentActe, TiffPage } from '@mairistem/administres-utils';
import { urlDocumentBinaire } from '@mairistem/administres-utils';

import {
  documentBinaireToApercu,
  type FormatDocument,
  type ReponseDocumentBinaire,
} from '@/utils/documentPreview';

/** Statut HTTP d'une erreur remontée par `request` (axios en dessous). */
const statutHttp = (erreur: unknown): number | undefined =>
  (erreur as { response?: { status?: number } })?.response?.status;

/** Aperçu décodé, avec le document auquel il appartient. */
interface ApercuCharge {
  identifiant?: number;
  pages: Array<TiffPage>;
  format: FormatDocument;
  binaire?: Uint8Array;
  /**
   * Le document existe mais n'a aucun contenu, ni pièce binaire ni RTF.
   *
   * Ce n'est pas une panne : la reprise de données a laissé 8 340 documents
   * dans cet état, l'API répond alors 404. Les distinguer évite d'annoncer une
   * erreur de chargement pour un document qui n'a simplement rien à montrer.
   */
  sansContenu: boolean;
}

const AUCUN_APERCU: ApercuCharge = {
  pages: [],
  format: 'inconnu',
  sansContenu: false,
};

/**
 * Pages affichables d'un document, décodées depuis son binaire.
 *
 * Porté par l'écran plutôt que par chacun de ses onglets : la liste et
 * l'annotation affichent le même document, et le télécharger deux fois le
 * rechargeait entièrement à chaque bascule.
 *
 * Le décodage est relancé sur le seul identifiant du document : rafraîchir la
 * liste (après un renommage, par exemple) ne doit pas retélécharger le binaire.
 */
const useApercuDocument = (document?: DocumentActe) => {
  const [charge, setCharge] = React.useState<ApercuCharge>(AUCUN_APERCU);
  const [chargement, setChargement] = React.useState(false);

  const identifiant = document?.identifiant;
  const type = document?.type;

  React.useEffect(() => {
    if (!identifiant) {
      setCharge(AUCUN_APERCU);
      return undefined;
    }

    let vivant = true;
    setChargement(true);

    request
      .get<unknown, ReponseDocumentBinaire>(urlDocumentBinaire(identifiant), {
        headers: { Accept: 'application/json' },
      })
      .then((reponse) => {
        /*
         * ⚠️ `request` de `@mairistem/tools` n'est pas une `Promise` : ses
         * `.then` sont empilés puis réduits de façon **synchrone**
         * (`listener(result) || result`, cf. `classnames/requests/Request.js`),
         * sans jamais attendre une promesse retournée. Chaîner un second
         * `.then` sur un traitement asynchrone lui livrerait la `Promise`
         * elle-même, pas sa valeur.
         */
        documentBinaireToApercu(reponse, type)
          .then((apercu) => {
            if (!vivant) return;

            setCharge({
              identifiant,
              format: apercu.format,
              binaire: apercu.binaire,
              sansContenu: false,
              pages: apercu.pages.map((page, index) => ({
                src: page.src,
                pageNumber: index + 1,
              })),
            });

            // Le DOCX et le Word 97-2003 n'ont pas de pages : leur panneau
            // dédié affiche le document ou propose de le télécharger.
            if (
              _.isEmpty(apercu.pages) &&
              apercu.format !== 'docx' &&
              apercu.format !== 'doc'
            ) {
              toast("Ce format de document n'a pas d'aperçu disponible", {
                type: 'warning',
              });
            }
          })
          .catch(() => {
            if (!vivant) return;
            setCharge({ ...AUCUN_APERCU, identifiant });
            toast('Erreur lors du décodage du document', { type: 'error' });
          })
          .finally(() => {
            if (vivant) setChargement(false);
          });
      })
      .catch((erreur: unknown) => {
        if (!vivant) return;
        setChargement(false);

        // 404 : l'API a répondu que ce document n'a aucun contenu. Le signaler
        // comme une erreur laisserait croire à une panne, alors qu'il n'y a
        // rien à charger.
        if (statutHttp(erreur) === 404) {
          setCharge({ ...AUCUN_APERCU, identifiant, sansContenu: true });
          toast("Ce document n'a aucun contenu à afficher", {
            type: 'warning',
          });
          return;
        }

        setCharge({ ...AUCUN_APERCU, identifiant });
        toast('Erreur lors du chargement du document', { type: 'error' });
      });

    return () => {
      vivant = false;
    };
  }, [identifiant, type]);

  /*
    L'aperçu n'est rendu que s'il porte bien sur le document demandé. L'état ne
    peut être remis à zéro que dans un effet, donc au rendu suivant : sans cette
    comparaison, le document qui vient d'être sélectionné s'affiche un instant
    avec le format du précédent. Assez pour que l'écran croie un JPEG annotable
    et aille chercher ses calques.
  */
  const correspond =
    !_.isNil(identifiant) && charge.identifiant === identifiant;

  return {
    pages: correspond ? charge.pages : [],
    format: correspond ? charge.format : ('inconnu' as FormatDocument),
    binaire: correspond ? charge.binaire : undefined,
    sansContenu: correspond ? charge.sansContenu : false,
    // Un document demandé dont l'aperçu n'est pas encore là est en cours de
    // chargement, y compris avant que l'effet n'ait démarré la requête.
    chargement: chargement || (!_.isNil(identifiant) && !correspond),
  };
};

export default useApercuDocument;
